import { useCallback, useEffect, useState } from 'react'
import type { Site, TimeEntry, User } from '@shared/types'
import { get } from '../../lib/api'
import {
  formatDate, formatDuration, formatTime, localDateKey, shiftDateKey, toDecimalHours,
} from '../../lib/format'
import { useSession } from '../../state/session'
import { useToast } from '../../state/toast'
import { Button, EmptyState, Field, SectionCard, Spinner } from '../../components/ui'
import { EntrySheet } from './EntrySheet'

interface TimesheetResponse {
  from: string
  to: string
  timezone: string
  entries: TimeEntry[]
  byUser: { userId: string; userName: string; ms: number; open: number }[]
  byUserSite: { userId: string; userName: string; siteId: string; siteName: string; ms: number }[]
}

/** null = sheet closed, 'new' = backfill a missed shift, otherwise the entry to fix. */
type SheetState = null | 'new' | TimeEntry

export default function AdminTimesheet() {
  const { settings } = useSession()
  const toast = useToast()
  const today = localDateKey(new Date(), settings.timezone)

  const [from, setFrom] = useState(() => shiftDateKey(today, -6))
  const [to, setTo] = useState(today)
  const [userId, setUserId] = useState('')
  const [siteId, setSiteId] = useState('')
  const [data, setData] = useState<TimesheetResponse | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [sheet, setSheet] = useState<SheetState>(null)

  const load = useCallback(async () => {
    const query = new URLSearchParams({ from, to })
    if (userId) query.set('userId', userId)
    if (siteId) query.set('siteId', siteId)
    setData(await get<TimesheetResponse>(`/api/admin/timesheet?${query}`))
  }, [from, to, userId, siteId])

  useEffect(() => {
    void Promise.all([
      get<{ users: User[] }>('/api/admin/users'),
      get<{ sites: Site[] }>('/api/admin/sites'),
    ]).then(([u, s]) => {
      setUsers(u.users)
      setSites(s.sites)
    }).catch(() => undefined)
  }, [])

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  const exportCsv = () => {
    if (!data) return
    const rows = [
      ['Date', 'Crew', 'Site', 'Clock in', 'Clock out', 'Hours', 'Source', 'Edit reason'],
      ...data.entries.map((entry) => [
        formatDate(entry.clockInAt, data.timezone),
        entry.userName ?? '',
        entry.siteName ?? '',
        formatTime(entry.clockInAt, data.timezone),
        entry.clockOutAt ? formatTime(entry.clockOutAt, data.timezone) : 'still open',
        toDecimalHours(entry.durationMs),
        entry.source,
        entry.editReason ?? '',
      ]),
    ]
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `windmill-hours-${from}-to-${to}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (!data) return <div className="py-16"><Spinner /></div>

  const totalMs = data.byUser.reduce((n, row) => n + row.ms, 0)

  return (
    <div className="space-y-5">
      {/* The range and filters — one quiet toolbar, not a section of its own. */}
      <div className="card p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="From">
            <input className="field font-mono" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <input className="field font-mono" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Crew">
            <select className="field" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Everyone</option>
              {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
            </select>
          </Field>
          <Field label="Site">
            <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">All sites</option>
              {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <SectionCard
        title="By person"
        action={
          <span className="font-mono text-lg font-semibold tnum">
            {toDecimalHours(totalMs)} h total
          </span>
        }
        flush
      >
        {data.byUser.length === 0 ? (
          <div className="p-4">
            <EmptyState title="No hours in this range" body="Widen the dates or clear the filters." />
          </div>
        ) : (
          <div className="scroll-x">
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Crew</th>
                  <th className="text-right">Hours</th>
                  <th className="text-right">Decimal</th>
                </tr>
              </thead>
              <tbody>
                {data.byUser.map((row) => (
                  <tr key={row.userId}>
                    <td className="font-semibold">
                      {row.userName}
                      {row.open > 0 && <span className="chip ml-2 bg-brand text-ink">on the clock</span>}
                    </td>
                    <td className="text-right font-mono tnum">{formatDuration(row.ms)}</td>
                    <td className="text-right font-mono font-semibold tnum">{toDecimalHours(row.ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {data.byUserSite.length > 0 && (
        <SectionCard title="By person and site" flush>
          <div className="scroll-x">
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Crew</th>
                  <th>Site</th>
                  <th className="text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {data.byUserSite.map((row) => (
                  <tr key={`${row.userId}-${row.siteId}`}>
                    <td>{row.userName}</td>
                    <td className="text-graphite-2">{row.siteName}</td>
                    <td className="text-right font-mono tnum">{toDecimalHours(row.ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      <SectionCard
        title="Every entry"
        action={
          <div className="flex gap-2">
            <Button className="min-h-9 px-3 text-xs" onClick={() => setSheet('new')}>
              Add entry
            </Button>
            <Button className="min-h-9 px-3 text-xs" onClick={exportCsv} disabled={!data.entries.length}>
              Export CSV
            </Button>
          </div>
        }
        flush
      >
        {data.entries.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="No entries"
              body="Nothing was clocked in this range. Add entry backfills a shift somebody forgot."
            />
          </div>
        ) : (
          <div className="scroll-x">
            <table className="grid-table min-w-max">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Crew</th>
                  <th>Site</th>
                  <th>In</th>
                  <th>Out</th>
                  <th className="text-right">Hours</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="whitespace-nowrap">{formatDate(entry.clockInAt, data.timezone)}</td>
                    <td className="font-semibold">{entry.userName}</td>
                    <td className="text-graphite-2">{entry.siteName}</td>
                    <td className="font-mono tnum">{formatTime(entry.clockInAt, data.timezone)}</td>
                    <td className="font-mono tnum">
                      {entry.clockOutAt
                        ? formatTime(entry.clockOutAt, data.timezone)
                        : <span className="chip bg-brand text-ink">open</span>}
                    </td>
                    <td className="text-right font-mono tnum">{toDecimalHours(entry.durationMs)}</td>
                    <td>
                      <button type="button" onClick={() => setSheet(entry)} className="link text-xs">
                        Fix
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {sheet !== null && (
        <EntrySheet
          entry={sheet === 'new' ? null : sheet}
          users={users}
          sites={sites}
          timezone={data.timezone}
          onClose={() => setSheet(null)}
          onSaved={async (message) => {
            setSheet(null)
            toast.success(message)
            await load()
          }}
        />
      )}
    </div>
  )
}
